import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { CLabel, CInput, CSelect, CSwitch } from '@coreui/react';
import CIcon from '@coreui/icons-react';
import 'react-dates/initialize';
import { DateRangePicker, SingleDatePicker } from 'react-dates';
import moment from 'moment';
import Select from 'react-select';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

interface IDataTableFilterProps {
  filterValue: any;
  onFilterChange: (value: any, filter: string) => void;
  filterBy: string;
  filterByOptions: { label: string; key: string, triggerQueryOnSelect?: boolean }[];
  label?: string;
  placeholder?: string;
  showToggle?: boolean;
  isToggleOn?: boolean;
  setIsToggleOn?: (value: boolean) => void;
  toggleLabel?: string;
  clearIcon?: string;
  lazy?: boolean;
  reflectFiltersInUrl?: boolean;
  autocompleteResults?: any[];
  onOptionsSearch?: (query: string) => void;
  onActiveFilterChanged?: (filter: string) => void;
}

export const DataTableFilter: React.FunctionComponent<IDataTableFilterProps> = ({
  showToggle = false,
  isToggleOn,
  setIsToggleOn,
  toggleLabel = '',
  filterValue,
  filterBy,
  filterByOptions,
  onFilterChange,
  label = 'Filter by:',
  placeholder = '',
  clearIcon = 'cilFilterX',
  lazy = true,
  autocompleteResults, onActiveFilterChanged,
  onOptionsSearch,
  reflectFiltersInUrl = false,
}) => {
  const urlQueryParams = useQuery();
  const filterByParam = urlQueryParams.get('filterBy') || filterByOptions[0]?.key;
  const filterValueParam = JSON.parse(urlQueryParams.get('filterValue') || '""');

  const history = useHistory();
  const [activeFilter, setActiveFilter] = useState(filterBy);
  const [activeStatus, setActiveStatus] = useState(filterValue);
  const [dropdownValue, setDropdownValue] = useState('')
  const [autocompleteValue, setAutocompleteValue] = useState<string>('')
  const [toggleValue, setToggleValue] = useState(isToggleOn);
  const [filterWithValues, setFilterWithValues] = useState([]) as any;
  const [values, setValues] = useState([]) as any;
  const [focusedDateInput, setFocusedDateInput] = useState(null) as any;
  const [value, setValue] = useState(filterValue);
  const [selectedDate, setSelectedDate] = useState({
    startDate: filterValue.startDate ? moment(filterValue.startDate) : null,
    endDate: filterValue.endDate ? moment(filterValue.endDate) : null,
  }) as any;
  const [date, setDate] = useState(null) as any;
  const [showHubSuggestionContainer, setShowHubSuggestionContainer] = useState<boolean>(false);
  const [focusedDate, setFocusedDate] = useState(null) as any;

  useEffect(() => {
    setShowHubSuggestionContainer(!!autocompleteResults?.length);
  }, [autocompleteResults]);

  useEffect(() => {
    if (filterByOptions.length && !activeFilter) {
      if (filterByParam && filterValueParam) {
        setActiveFilter(filterByParam);
        if (filterByParam === 'dropdown') {
          const dropdownOptions = filterByOptions?.find((option: any) => option.key.match(/dropdown/)).values;
          const selectedOption = dropdownOptions?.find((option: any) => option.key === filterValueParam);
          setDropdownValue(selectedOption)
        } else if (filterByParam === 'hub' || filterByParam === 'partner') {
          setAutocompleteValue(filterValueParam);
        }
        onFilterChange(filterValueParam, filterByParam);
      } else {
        setActiveFilter(filterByOptions[0].key);
      }
    }
  }, [filterByOptions]);

  const handleFilterChange = ({ target: { value } }: any, type: string): void => {
    if ((lazy && type === 'input') || (!lazy && type === 'change')) {
      return;
    }
    setValue(value);
    onFilterChange(value, activeFilter);
  };

  useEffect(() => {
    setToggleValue(isToggleOn);
    setActiveFilter(filterBy);
    if (onActiveFilterChanged) {
      onActiveFilterChanged(filterBy);
    }
    setSelectedDate({
      startDate: filterValue.startDate ? moment(filterValue.startDate) : null,
      endDate: filterValue.endDate ? moment(filterValue.endDate) : null,
    });
    if (typeof filterValue === 'string') {
      setActiveStatus(filterValue);
      setValue(filterValue);
      setAutocompleteValue(filterValueParam);
    }
  }, [isToggleOn, filterBy, filterValue]);

  useEffect(() => {
    const statusData: any = filterByOptions?.find((option: any) => option.key.match(/status/) && option.key === activeFilter);
    if (statusData) {
      const statusesArray = Object.keys(statusData?.values).map((value) => ({
        key: value,
        label: statusData?.values[value],
      }));
      setValues([{ key: 'ALL', label: 'All' }, ...statusesArray]);
    }

    const filterWithValues: any = filterByOptions?.find((option: any) => !option.key.match(/status/) && !option.key.match(/dropdown/) && !option.key.match(/autocomplete/) && option?.values);
    if (filterWithValues) {
      const values = Object.keys(filterWithValues?.values).map((value) => ({
        key: value,
        label: filterWithValues?.values[value],
      }));
      setFilterWithValues(values);
      setValues(values);
    }
  }, [filterByOptions, setValues, setFilterWithValues, activeFilter]);

  const onSuggestionBlur = () => {
    setShowHubSuggestionContainer(false);
    return false;
  };

  const updateUrlFilters = (filter, value) => {
    if (reflectFiltersInUrl) {
      if (value !== '') {
        urlQueryParams.set('filterBy', filter);
        urlQueryParams.set('filterValue', JSON.stringify(value));
      } else {
        urlQueryParams.delete('filterBy');
        urlQueryParams.delete('filterValue');
      }
      history.push({
        search: urlQueryParams.toString(),
      });
    }
  }

  return (
    <div className="form-inline justify-content-sm-end">
      {showToggle && (
        <>
          <CLabel className="mr-2">{toggleLabel}</CLabel>
          <CSwitch
            color="primary"
            className="d-block mr-1"
            checked={toggleValue}
            onChange={() => {
              setIsToggleOn?.(!toggleValue);
              setToggleValue(!toggleValue);
            }}
            size="lg"
            shape="pill"
          />
        </>
      )}
      <CLabel className="mr-2">{label}</CLabel>
      {
        filterWithValues.length === 0 && (
          <CSelect
            name="filterBy"
            className="form-control mr-1"
            onChange={({ target: { value } }: any) => {
              setActiveFilter(value);
              if (onActiveFilterChanged) {
                onActiveFilterChanged(value);
              }
              setValue('');
              setAutocompleteValue('')
              setSelectedDate({
                startDate: null,
                endDate: null,
              });
              const filter = filterByOptions.find(i => i.key === value);
              if (filter?.triggerQueryOnSelect) {
                onFilterChange('', value);
              }
            }}
            value={activeFilter}
          >
            {filterByOptions.map(({ label, key }) => (
              <option value={key} key={key}>
                {label}
              </option>
            ))}
          </CSelect>
        )}
      {activeFilter.match(/dateRange/) && (
        <DateRangePicker
          minimumNights={0}
          startDate={selectedDate.startDate}
          startDateId="startDate"
          endDate={selectedDate.endDate}
          endDateId="endDate"
          onDatesChange={({ startDate, endDate }: any) => {
            setSelectedDate({ startDate, endDate });
            if (startDate && endDate) {
              onFilterChange({
                startDate: moment(startDate).format('YYYY-MM-DDT00:00:00'),
                endDate: moment(endDate).format('YYYY-MM-DDT23:59:59'),
              }, activeFilter);
            }
          }}
          minDate={moment('2020-01-01')}
          focusedInput={focusedDateInput}
          onFocusChange={focusedInput => setFocusedDateInput(focusedInput)}
          hideKeyboardShortcutsPanel
          isOutsideRange={() => false}
          displayFormat="DD-MM-YYYY"
          small
        />
      )}
      {activeFilter === 'date' && (
        <SingleDatePicker
          date={date}
          onDateChange={(date) => {
            setDate(date);
            onFilterChange(moment(date).format('YYYY-MM-DDTHH:mm:ss'), activeFilter);
          }}
          focused={focusedDate}
          onFocusChange={({ focused }) => setFocusedDate(focused)}
          isOutsideRange={() => false}
          hideKeyboardShortcutsPanel
          displayFormat="DD-MM-YYYY"
          id="date"
          small
        />
      )}
      {(activeFilter.match(/status/)|| (filterWithValues.length !== 0 && !activeFilter.match(/dropdown/) && !activeFilter.match(/hub/) && !activeFilter.match(/partner/))) && (
        <CSelect
          name="filterByStatus"
          className="form-control mr-2"
          onChange={({ target: { value } }: any) => {
            setActiveStatus(value);
            if (value === 'ALL') {
              onFilterChange('', activeFilter);
            } else {
              onFilterChange(value, activeFilter);
            }
          }}
          value={activeStatus}
        >
          {values.map(({ key, label }: any) => (
            <option value={key} key={key}>
              {label}
            </option>
          ))}
        </CSelect>
      )}
      {(activeFilter.match(/hub/) || activeFilter.match(/partner/)) && onOptionsSearch && (
        <div className="suggestion" onBlur={() => onSuggestionBlur()}>
          <CInput
            id="hub-autosuggest"
            name="hubInput"
            type="text"
            autoComplete="off"
            onChange={(event) => {
              setAutocompleteValue(event.target.value);
              onOptionsSearch(event.target.value);
            }}
            value={autocompleteValue}
          />
          <CIcon
              name={clearIcon}
              onClick={() => {
                onFilterChange('', activeFilter);
                updateUrlFilters(activeFilter, '');
                setShowHubSuggestionContainer(false);
                setAutocompleteValue('');
              }}
          />
          {showHubSuggestionContainer && (
            <div className="suggestion-container">
              {autocompleteResults?.length && autocompleteResults.map((result: any, index: number) => (
                  <div className="suggestion-item p-2"
                   key={index}
                   onMouseDown={(e) => e.preventDefault()}
                   onClick={() => {
                     setAutocompleteValue(result.name)
                     onFilterChange(result.id, activeFilter);
                     updateUrlFilters(activeFilter, result.id);
                     setShowHubSuggestionContainer(false);

                   }}>
                <span className="secondary-text">{result.name}</span>
              </div>))}
            </div>
          )}
    </div>
      )}
      {(activeFilter.match(/dropdown/)) && (
          <CSelect
              name="filterByValues"
              className="form-control mr-1 dropdown-filter"
              onChange={({ target: { value } }: any) => {
                setDropdownValue(value);
                onFilterChange(value, activeFilter);
                updateUrlFilters(activeFilter, value);
              }}
              value={dropdownValue}
          >
            {filterByOptions?.find((option: any) => option.key.match(/dropdown/)).values.map(({ key, label }: any) => (
                <option value={key} key={key}>
                  {label}
                </option>
            ))}
          </CSelect>
      )}
      {(activeFilter.match(/autocomplete/)) && (
          <Select
              name="filterByValues"
              onChange={(value: any) => {
                onFilterChange(value, activeFilter);
              }}
              options={filterByOptions?.find((option: any) => option.key.match(/autocomplete/)).values}
          />
      )}
      {!activeFilter.match(/status/) &&
          !activeFilter.match(/dateRange/) &&
          !activeFilter.match(/dropdown/) &&
          (!activeFilter.match(/hub/) || (activeFilter.match(/hub/) && !onOptionsSearch)) &&
          (!activeFilter.match(/partner/) || (activeFilter.match(/partner/) && !onOptionsSearch)) &&
          activeFilter !== 'date' && (
        <>
          <CInput
            className={`form-control mr-1`}
            type="text"
            placeholder={placeholder}
            onInput={(e) => {
              handleFilterChange(e, 'input');
            }}
            onChange={(e) => {
              handleFilterChange(e, 'change');
            }}
            value={value}
          />
          <CIcon
            name={clearIcon}
            onClick={() => {
              onFilterChange('', activeFilter);
              setValue('');
            }}
          />
        </>
      )}
    </div>
  );
};
